import type { MonthYear } from '@seek/ca-myprofile-utils';
import { Qualification } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  Heading,
  Notice,
  Stack,
  Text,
} from 'braid-design-system';
import { v4 as uuid } from 'uuid';

import type { Qualification as QualificationType } from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import { logError } from 'src/modules/error-logger/browserLog';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';
import { useEducationMutations } from './hooks/useEducationMutations';
import { useTracking } from './hooks/useTracking';

interface Props {
  qualifications: QualificationType[];
  unconfirmedQualifications: QualificationType[];
  shouldShowPrompt?: boolean;
  shouldShowHeader?: boolean;
  hideWhenEditing?: boolean;
}

export function EducationList({
  qualifications,
  unconfirmedQualifications,
  shouldShowPrompt = false,
  shouldShowHeader = false,
  hideWhenEditing = false,
}: Props) {
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const { onEditorOpen, editingId } = useEditorDrawer();
  const allQualifications = [...unconfirmedQualifications, ...qualifications];
  const confirmedIdsSet = new Set(qualifications.map(({ id }) => id));
  const unconfirmedIdsSet = new Set(
    unconfirmedQualifications.map(({ id }) => id),
  );
  const { rejectQualification, confirmQualification } = useEducationMutations();
  const { trackAddQualification, trackRejectQualification, listTrackingRef } =
    useTracking({
      qualifications,
      editingId,
    });

  const handleReject = async (id: string) => {
    if (!unconfirmedIdsSet.has(id)) {
      return;
    }
    try {
      await rejectQualification(id);
      trackRejectQualification();
    } catch (err: any) {
      logError({ ...err, type: 'reject-qualification' }, { error: err });
    }
  };

  const handleConfirm = async (id: string) => {
    const values = unconfirmedQualifications.find((q) => q.id === id);
    if (!values) {
      return;
    }

    try {
      await confirmQualification(values);
      trackAddQualification('cv parsed');
    } catch (err: any) {
      logError({ ...err, type: 'confirm-qualification' }, { error: err });
    }
  };

  if (hideWhenEditing && editingId) {
    return null;
  }

  return (
    <Box ref={listTrackingRef}>
      <Stack space="large">
        {shouldShowHeader && <Heading level="3">{t('Education')}</Heading>}
        {shouldShowPrompt && allQualifications.length === 0 && (
          <Text tone="secondary">
            {t('Tell employers about your education')}
          </Text>
        )}
        {unconfirmedQualifications.length > 0 && (
          <Notice tone="promote">
            <Text>
              {t(
                `We found these qualifications in your resumé. Add them to your`,
                { brand: config.BRAND },
              )}
            </Text>
          </Notice>
        )}
        <Stack component="ul" space="small">
          {allQualifications.map((quali) => {
            const education = quali as QualificationType;
            const isConfirmed = confirmedIdsSet.has(quali.id);

            return (
              <li key={`${education.id}-${isConfirmed}`}>
                <Qualification
                  zone={config.ZONE}
                  id={education.id}
                  displayVariant="card"
                  confirmed={confirmedIdsSet.has(quali.id)}
                  // @ts-expect-error name.ontologyId mistmatch
                  name={education.name}
                  // @ts-expect-error name.ontologyId mistmatch
                  institute={education.institute}
                  // @ts-expect-error name.ontologyId mistmatch
                  highlights={education.highlights}
                  // @ts-expect-error null mistmatch
                  credential={education.credential}
                  // @ts-expect-error null mistmatch
                  status={education.status}
                  // these 3 props can be just one
                  completed={education.completed}
                  completionDate={education.completionDate as MonthYear}
                  formattedCompletion={education.formattedCompletion}
                  // these 3 props can be just one
                  editQualification={(id) => onEditorOpen(id)}
                  rejectUnconfirmedQualification={handleReject}
                  addUnconfirmedQualification={handleConfirm}
                />
              </li>
            );
          })}
        </Stack>
        <Actions>
          <Button
            tone="formAccent"
            variant="ghost"
            data={{ automation: 'add-education' }}
            onClick={() => onEditorOpen(uuid())}
          >
            {t('Add education')}
          </Button>
        </Actions>
      </Stack>
    </Box>
  );
}
